import React, { useEffect, useState } from 'react';
import { currencySymbol } from '../../../util/money';
import './MoneyInput.css';

/** Number from the parent's dot-decimal value string ('' → 0, no thousands). */
function valueToNumber(value: string | null | undefined): number {
    const n = Number((value ?? '').toString().replace(',', '.'));
    return Number.isNaN(n) ? 0 : n;
}

/** Number from what's shown in the box (BR format: '.' thousands, ',' decimal). */
function displayToNumber(text: string): number {
    const n = Number(text.replace(/\./g, '').replace(',', '.'));
    return Number.isNaN(n) ? 0 : n;
}

/** Group an integer-digits string with BR thousands dots: 898989 → 898.989. */
function groupThousands(intDigits: string): string {
    return intDigits.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
}

/**
 * Format a parent value (dot-decimal number string) into the BR display:
 * thousands '.', decimal ','. A zero (or blank) value renders EMPTY so the field
 * never starts with a stuck "0" — the "0,00" placeholder carries the hint.
 */
function toDisplay(value: string | null | undefined): string {
    if (value == null || value === '') return '';
    if (valueToNumber(value) === 0) return '';
    const [intRaw, decRaw] = value.toString().replace(',', '.').split('.');
    const grouped = groupThousands((intRaw || '').replace(/\D/g, '')) || '0';
    return decRaw != null ? `${grouped},${decRaw}` : grouped;
}

interface MoneyInputProps {
    /** Current value as a string (controlled — empty string for blank). */
    value: string;
    /** Fired with the raw string the user typed. Parent decides when to parse. */
    onChange: (next: string) => void;
    /** ISO code (USD, BRL...). Resolves to the prefix symbol. */
    currency?: string | null;
    /** Optional id for label htmlFor wiring. */
    id?: string;
    /** Placeholder for the input (defaults to "0.00"). */
    placeholder?: string;
    /** Disabled state. */
    disabled?: boolean;
    /** Min value (default 0). */
    min?: number;
    /** Step (default 0.01 for 2-decimal money). */
    step?: number;
    /** Optional trailing slot (e.g. a "Pay all" link). */
    trailing?: React.ReactNode;
    /** Forward className onto the wrapper (e.g. for layout). */
    className?: string;
    /** Forwarded autoFocus. */
    autoFocus?: boolean;
    /** Forward onBlur if the parent wants to normalize on blur. */
    onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
    /** Optional Cypress selector for the input. Defaults to `'money-input'`. */
    dataCy?: string;
}

/**
 * Money input with a currency-symbol prefix, identical to the one used
 * in the Calendar Payment tab. Use this anywhere the user enters an
 * amount of money (service price, fees, manual payment splits…).
 *
 *   <MoneyInput
 *       currency={selectedBusiness?.currency}
 *       value={priceStr}
 *       onChange={setPriceStr}
 *   />
 */
const MoneyInput: React.FC<MoneyInputProps> = ({
    value,
    onChange,
    currency,
    id,
    placeholder = '0.00',
    disabled = false,
    trailing,
    className,
    autoFocus,
    onBlur,
    dataCy,
}) => {
    // The box keeps its OWN text so live BR formatting (thousands '.', decimal
    // ',') and an in-progress decimal ("2000," / "2000,5") survive even when the
    // parent rounds the emitted value through Number() and feeds back
    // String(number) — which would otherwise eat the comma the instant it was
    // typed (issue #33). type="text" + inputMode="decimal" since a number input
    // rejects separators. Always EMIT plain dot-decimal (no thousands) so every
    // parent (which does Number(value)) keeps working unchanged.
    const [text, setText] = useState<string>(() => toDisplay(value));

    // Re-sync from the parent ONLY when it pushes a genuinely different number
    // (external set, reset, edit-load) — never mid-typing, where the numeric
    // value already matches what we show (so "2.000," isn't clobbered to "2000").
    useEffect(() => {
        if (valueToNumber(value) !== displayToNumber(text)) {
            setText(toDisplay(value));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value]);

    const handleChange = (raw: string) => {
        // BR convention: '.' is a thousands separator (dropped), ',' the decimal.
        const cleaned = raw.replace(/[^\d,]/g, '');
        const comma = cleaned.indexOf(',');
        let intPart = (comma === -1 ? cleaned : cleaned.slice(0, comma)).replace(/,/g, '');
        // Money has at most 2 decimal places — cap it so a repeating decimal
        // (e.g. 19,333333…) can't be typed/stored.
        const decPart = comma === -1 ? null : cleaned.slice(comma + 1).replace(/,/g, '').slice(0, 2);
        intPart = intPart.replace(/^0+(?=\d)/, ''); // drop leading zeros (08 → 8)
        // What the user sees: grouped integer + (comma + decimals when present).
        let display = groupThousands(intPart);
        if (comma !== -1) display += `,${decPart}`;
        setText(display);
        // What the parent stores: plain dot-decimal, Number()-friendly.
        let emit = intPart;
        if (decPart) emit += `.${decPart}`;
        onChange(emit);
    };
    return (
        <div className={`money-input ${className ?? ''}`.trim()}>
            <span className="money-input__symbol">{currencySymbol(currency)}</span>
            <input
                id={id}
                type="text"
                inputMode="decimal"
                placeholder={(placeholder ?? '').replace('.', ',')}
                value={text}
                onChange={(e) => handleChange(e.target.value)}
                disabled={disabled}
                className="form-control money-input__field"
                autoFocus={autoFocus}
                onBlur={onBlur}
                data-cy={dataCy ?? 'money-input'}
            />
            {trailing && <div className="money-input__trailing">{trailing}</div>}
        </div>
    );
};

export default MoneyInput;
